import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LoanKind,
  type CreateLoanRequest,
  type Loan,
  type LoanPaymentResult,
  type RegisterLoanPaymentRequest,
  type UpdateLoanRequest,
} from '@financial/shared';
import { api } from '../../lib/api';
import { notify } from '../../store/notifications';

const KEY = ['loans'];

async function listLoans(): Promise<Loan[]> {
  const res = await api.get<Loan[]>('/loans');
  return res.data;
}

export function useLoans() {
  return useQuery({ queryKey: KEY, queryFn: listLoans });
}

function useInvalidate() {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: KEY }),
      qc.invalidateQueries({ queryKey: ['accounts'] }),
      qc.invalidateQueries({ queryKey: ['transactions'] }),
      qc.invalidateQueries({ queryKey: ['net-worth'] }),
      qc.invalidateQueries({ queryKey: ['budget'] }),
      qc.invalidateQueries({ queryKey: ['insights'] }),
    ]);
  };
}

export function useCreateLoan() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: CreateLoanRequest) => {
      const res = await api.post<Loan>('/loans', body);
      return res.data;
    },
    onSuccess: (loan) => {
      void invalidate();
      notify.success('Deuda o préstamo creado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await api.delete(`/loans/${loan.id}`);
            await invalidate();
            notify.info('Registro deshecho');
          },
        },
      });
    },
  });
}

export function useUpdateLoan() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (vars: { id: string; body: UpdateLoanRequest }) => {
      const res = await api.patch<Loan>(`/loans/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: () => {
      void invalidate();
      notify.success('Deuda o préstamo actualizado');
    },
  });
}

export function usePayLoan() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ loanId, body }: { loanId: string; body: RegisterLoanPaymentRequest }) => {
      const res = await api.post<LoanPaymentResult>(`/loans/${loanId}/pay`, body);
      return res.data;
    },
    onSuccess: (result, variables) => {
      void invalidate();
      const isDebt = result.loan.kind === LoanKind.DEBT;
      const action = isDebt ? 'Pago' : 'Cobro';
      const movementSuffix = variables.body.registerMovement
        ? ' y movimiento registrados'
        : ' marcado';
      notify.success(`${action}${movementSuffix}`, {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await api.delete<LoanPaymentResult>(
              `/loans/${variables.loanId}/payments/${result.payment.id}`,
            );
            await invalidate();
            notify.info(`${action} deshecho`);
          },
        },
      });
    },
  });
}

export function useDeleteLoan() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/loans/${id}`);
    },
    onSuccess: () => {
      void invalidate();
      notify.success('Deuda o préstamo eliminado');
    },
  });
}
